import "./native-app-promo-modal.scss";

import { regular } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMemo, useState } from "react";

import Modal from "components/modal/Modal";

import {
  NATIVE_APP_PROMO_APP_STORE_BADGE_URL,
  NATIVE_APP_PROMO_APP_STORE_URL,
  NATIVE_APP_PROMO_GET_APP_BUTTON_LABEL,
  NATIVE_APP_PROMO_GOOGLE_PLAY_BADGE_URL,
  NATIVE_APP_PROMO_GOOGLE_PLAY_URL,
  NATIVE_APP_PROMO_MODAL_HELPER_TEXT,
  NATIVE_APP_PROMO_MODAL_MAYBE_LATER,
  NATIVE_APP_PROMO_MODAL_TITLE,
  NATIVE_APP_PROMO_POINT_CAMERA_HELPER,
  NATIVE_APP_PROMO_QR_FALLBACK_TEXT,
  NATIVE_APP_PROMO_QR_IMAGE_URL,
  NATIVE_APP_PROMO_SCAN_TO_DOWNLOAD,
  NATIVE_APP_PROMO_SUBTITLE,
  NATIVE_APP_PROMO_TITLE,
} from "utilities/Constant";

export type DownloadLinkType = "appStore" | "googlePlay";

export type NativeAppPromoContentVariant = "cardDesktop" | "cardMobile" | "modal";

const downloadLinks: {
  type: DownloadLinkType;
  href: string;
  label: string;
  badgeSrc: string;
  badgeAlt: string;
}[] = [
    {
      type: "appStore",
      href: NATIVE_APP_PROMO_APP_STORE_URL,
      label: "Download on the App Store",
      badgeSrc: NATIVE_APP_PROMO_APP_STORE_BADGE_URL,
      badgeAlt: "Download on the App Store",
    },
    {
      type: "googlePlay",
      href: NATIVE_APP_PROMO_GOOGLE_PLAY_URL,
      label: "Get it on Google Play",
      badgeSrc: NATIVE_APP_PROMO_GOOGLE_PLAY_BADGE_URL,
      badgeAlt: "Get it on Google Play",
    },
  ];

export function DownloadBadges({
  onImageError,
  showAppStore,
  showGooglePlay,
}: {
  readonly onImageError: (type: DownloadLinkType) => void;
  readonly showAppStore: boolean;
  readonly showGooglePlay: boolean;
}) {
  return (
    <div className="native-app-promo-card__ctas">
      {downloadLinks.map((link) => {
        const isVisible = link.type === "appStore" ? showAppStore : showGooglePlay;
        if (!isVisible) return null;

        return (
          <a key={link.type} href={link.href} target="_blank" rel="noreferrer" aria-label={link.label}>
            <img
              src={link.badgeSrc}
              alt={link.badgeAlt}
              loading="lazy"
              onError={() => onImageError(link.type)}
            />
          </a>
        );
      })}
    </div>
  );
}

export function QrBlock({
  showQr,
  onQrError,
}: {
  readonly showQr: boolean;
  readonly onQrError: () => void;
}) {
  if (!showQr) {
    return (
      <div
        className="paragraph-small-regular native-app-promo-card__qr native-app-promo-card__qr--fallback">
        {NATIVE_APP_PROMO_QR_FALLBACK_TEXT}
      </div>
    );
  }

  return (
    <div className="native-app-promo-card__qr">
      <img
        src={NATIVE_APP_PROMO_QR_IMAGE_URL}
        alt="NAF Hub app QR code"
        loading="lazy"
        onError={onQrError}
      />
    </div>
  );
}

export function NativeAppPromoContent({
  variant,
  showQr,
  onQrError,
  showAppStoreBadge,
  showGooglePlayBadge,
  onBadgeError,
  onPrimaryCtaClick,
  onSecondaryCtaClick,
}: {
  readonly variant: NativeAppPromoContentVariant;
  readonly showQr: boolean;
  readonly onQrError: () => void;
  readonly showAppStoreBadge: boolean;
  readonly showGooglePlayBadge: boolean;
  readonly onBadgeError: (type: DownloadLinkType) => void;
  readonly onPrimaryCtaClick?: () => void;
  readonly onSecondaryCtaClick?: () => void;
}) {
  const isCard = variant === "cardDesktop" || variant === "cardMobile";

  return (
    <div className={isCard ? "native-app-promo-card__content" : "native-app-promo-modal__body custom-full-content"}>
      {isCard && (
        <div className="native-app-promo-card__text">
          <div className="label-large-medium native-app-promo-card__title">{NATIVE_APP_PROMO_TITLE}</div>
          <div className="label-alt-medium-medium native-app-promo-card__subtitle">{NATIVE_APP_PROMO_SUBTITLE}</div>
        </div>
      )}

      {variant === "cardDesktop" && (
        <>
          <div className="native-app-promo-card__desktop">
            <QrBlock showQr={showQr} onQrError={onQrError} />
          </div>
          <div className="native-app-promo-card__qr-text">
            <div className="label-medium-medium native-app-promo-card__qr-title">{NATIVE_APP_PROMO_SCAN_TO_DOWNLOAD}</div>
            <div className="label-alt-medium-medium native-app-promo-card__qr-helper">{NATIVE_APP_PROMO_POINT_CAMERA_HELPER}</div>
          </div>
        </>
      )}

      {variant === "cardMobile" && (
        <button
          type="button"
          className="label-small-medium native-app-promo-card__get-app"
          onClick={onPrimaryCtaClick}
          aria-label={NATIVE_APP_PROMO_GET_APP_BUTTON_LABEL}
        >
          {NATIVE_APP_PROMO_GET_APP_BUTTON_LABEL}
        </button>
      )}

      {variant === "modal" && (
        <>
          <div className="native-app-promo-modal__qr">
            <QrBlock showQr={showQr} onQrError={onQrError} />
          </div>
          <div className="native-app-promo-modal__helper">{NATIVE_APP_PROMO_MODAL_HELPER_TEXT}</div>
        </>
      )}

      <div className={variant === "modal" ? "native-app-promo-modal__ctas" : undefined}>
        <DownloadBadges
          onImageError={onBadgeError}
          showAppStore={showAppStoreBadge}
          showGooglePlay={showGooglePlayBadge}
        />
      </div>

      {variant === "modal" && (
        <button
          type="button"
          className="label-medium-underlined native-app-promo-modal__maybe-later"
          onClick={onSecondaryCtaClick}
        >
          {NATIVE_APP_PROMO_MODAL_MAYBE_LATER}
        </button>
      )}
    </div>
  );
}

export function useNativeAppPromoState() {
  const [isQrOk, setIsQrOk] = useState(true);
  const [badgeOk, setBadgeOk] = useState<{ appStore: boolean; googlePlay: boolean }>({
    appStore: true,
    googlePlay: true,
  });

  const onBadgeError = (type: DownloadLinkType) => {
    setBadgeOk((prev) => ({ ...prev, [type]: false }));
  };

  const onQrError = () => setIsQrOk(false);

  const shouldShowBadges = useMemo(
    () => ({
      appStore: badgeOk.appStore,
      googlePlay: badgeOk.googlePlay,
    }),
    [badgeOk]
  );

  return { isQrOk, onQrError, shouldShowBadges, onBadgeError };
}

export default function NativeAppPromoModal({
  onClose,
  showQr,
  onQrError,
  onBadgeError,
  showAppStoreBadge,
  showGooglePlayBadge,
}: {
  readonly onClose: () => void;
  readonly showQr: boolean;
  readonly onQrError: () => void;
  readonly onBadgeError: (type: DownloadLinkType) => void;
  readonly showAppStoreBadge: boolean;
  readonly showGooglePlayBadge: boolean;
}) {
  return (
    <div className="native-app-promo-modal">
      <Modal
        customHeader={
          <div className="native-app-promo-modal__header">
            <h6 className="heading-medium native-app-promo-modal__title">{NATIVE_APP_PROMO_MODAL_TITLE}</h6>
            <button
              type="button"
              className="native-app-promo-modal__close"
              aria-label="Close"
              onClick={onClose}
            >
              <FontAwesomeIcon icon={regular("xmark")} />
            </button>
          </div>
        }
        customBody={
          <NativeAppPromoContent
            variant="modal"
            showQr={showQr}
            onQrError={onQrError}
            showAppStoreBadge={showAppStoreBadge}
            showGooglePlayBadge={showGooglePlayBadge}
            onBadgeError={onBadgeError}
            onSecondaryCtaClick={onClose}
          />
        }
      />
    </div>
  );
}
